/**
 * Portal Auth Provider
 * 
 * This provider abstracts authentication for the portal module.
 * 
 * CURRENT IMPLEMENTATION: Wraps the main app's AuthContext
 * FORK IMPLEMENTATION: Replace with standalone auth (Auth0, Firebase, etc.)
 * 
 * To fork:
 * 1. Remove the import from main AuthContext
 * 2. Implement your own auth state management
 * 3. Connect to your auth provider
 */

import React, { createContext, useMemo, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth as useMainAuth } from '../../hooks/useAuth';
import { supabase } from '../../integrations/supabase/client';
import { useClientView } from '../contexts/ClientViewContext';
import type { PortalAuthContextType, PortalAuthState, PortalAuthUser } from './types';

export const PortalAuthContext = createContext<PortalAuthContextType | null>(null);

interface PortalAuthProviderProps {
  children: ReactNode;
}

function toRoleSet(input: unknown): Set<string> {
  const set = new Set<string>();
  if (Array.isArray(input)) {
    input.forEach((r) => {
      if (typeof r === 'string') set.add(r);
    });
    return set;
  }
  if (typeof input === 'string') {
    set.add(input);
  }
  return set;
}

/**
 * Portal Auth Provider - Bridges to main app auth
 * Replace this entire component when forking
 */
export const PortalAuthProvider: React.FC<PortalAuthProviderProps> = ({ children }) => {
  // Bridge to main app auth
  const mainAuth = useMainAuth();
  const { session: clientViewSession, isViewingAsAdmin } = useClientView();

  const [portalUser, setPortalUser] = useState<PortalAuthUser | null>(null);
  const [portalUserLoading, setPortalUserLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState<{ clientId?: string; clientName?: string; isPlatformLevel?: boolean }>({});

  // Fetch additional portal-specific user data
  // Supports client view override for tenant admins and platform-level access
  useEffect(() => {
    let cancelled = false;

    const fetchPortalUserData = async () => {
      if (!mainAuth.user?.id) {
        setPortalUser(null);
        setClientInfo({});
        setPortalUserLoading(false);
        return;
      }

      setPortalUserLoading(true);

      try {
        const sessionUser: any = (mainAuth.session as any)?.user ?? (mainAuth.user as any);

        const tokenRoles = new Set<string>([
          ...toRoleSet(sessionUser?.app_metadata?.roles),
          ...toRoleSet(sessionUser?.user_metadata?.roles),
          ...toRoleSet(sessionUser?.app_metadata?.role),
          ...toRoleSet(sessionUser?.user_metadata?.role),
        ]);

        const tokenIsSuperAdmin =
          !!sessionUser?.user_metadata?.is_super_admin ||
          !!sessionUser?.app_metadata?.is_super_admin ||
          tokenRoles.has('super_admin');

        const tokenIsPlatformAdmin = tokenIsSuperAdmin || tokenRoles.has('platform_admin');

        // Get profile with client info and portal admin flag
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('first_name, last_name, client_id, is_portal_admin, platform_tenant_id')
          .eq('id', mainAuth.user.id)
          .single();

        if (profileError) {
          console.error('[PortalAuthProvider] Error fetching profile:', profileError);
        }

        // Check user_roles table for admin/owner role
        const { data: userRoles, error: userRolesError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', mainAuth.user.id);

        if (userRolesError) {
          console.error('[PortalAuthProvider] Error fetching user roles:', userRolesError);
        }

        const dbRoles = new Set<string>(
          (userRoles || [])
            .map((r: any) => r?.role)
            .filter((r: unknown): r is string => typeof r === 'string'),
        );

        // Determine admin status from roles (DB + token fallback)
        const isSuperAdmin = tokenIsSuperAdmin || dbRoles.has('super_admin');
        const isPlatformAdmin = isSuperAdmin || tokenIsPlatformAdmin || dbRoles.has('platform_admin');
        const hasAdminRole =
          isSuperAdmin ||
          isPlatformAdmin ||
          dbRoles.has('customer_admin') ||
          tokenRoles.has('customer_admin');

        const isPortalAdmin = !!profile?.is_portal_admin || hasAdminRole;

        // Use overridden client ID if in client view mode (single source of truth = ClientViewContext)
        const isViewingClient = isViewingAsAdmin && !!clientViewSession?.client_id;

        // IMPORTANT: Platform/Super admins should default to platform-level portal view.
        // They only get a client context when explicitly using Client View (impersonation).
        const clientIdToUse = isViewingClient
          ? clientViewSession?.client_id
          : (isPlatformAdmin ? undefined : profile?.client_id);

        let clientName: string | undefined;
        let tenantName: string | undefined;
        let isPlatformLevel = false;

        if (clientIdToUse) {
          // User has a client context
          if (isViewingClient && clientViewSession?.client_name) {
            clientName = clientViewSession.client_name;
          } else {
            const { data: client, error: clientError } = await supabase
              .from('clients')
              .select('name')
              .eq('id', clientIdToUse)
              .single();

            if (clientError) {
              console.error('[PortalAuthProvider] Error fetching client name:', clientError);
            }

            clientName = client?.name;
          }
        } else if (isPlatformAdmin) {
          // Platform admin without client context - show platform-level view
          isPlatformLevel = true;

          if (profile?.platform_tenant_id) {
            const { data: tenant, error: tenantError } = await supabase
              .from('platform_tenants')
              .select('company_name')
              .eq('id', profile.platform_tenant_id)
              .single();

            if (tenantError) {
              console.error('[PortalAuthProvider] Error fetching tenant name:', tenantError);
            }

            tenantName = tenant?.company_name || 'Platform';
            clientName = isSuperAdmin ? 'Super Admin' : `${tenantName} Admin`;
          } else {
            clientName = isSuperAdmin ? 'Super Admin' : 'Platform Admin';
          }
        }

        // Determine portal role
        let portalRole: 'owner' | 'admin' | 'member' | 'viewer' = 'member';
        if (isViewingClient) {
          portalRole = 'viewer'; // Client view mode = read-only
        } else if (isSuperAdmin) {
          portalRole = 'owner'; // Super admin gets full access
        } else if (isPlatformAdmin) {
          portalRole = 'owner'; // Platform admin gets owner-level access
        } else if (isPortalAdmin) {
          portalRole = 'owner'; // Portal admin gets owner-level access
        } else if (hasAdminRole) {
          portalRole = 'admin';
        }

        if (cancelled) return;

        setPortalUser({
          id: mainAuth.user.id,
          email: mainAuth.user.email || '',
          firstName: profile?.first_name || undefined,
          lastName: profile?.last_name || undefined,
          avatarUrl: undefined,
          clientId: clientIdToUse || undefined,
          clientName,
          platformTenantId: profile?.platform_tenant_id || undefined,
          role: portalRole,
          isPlatformAdmin,
          isSuperAdmin,
        });

        setClientInfo({
          clientId: clientIdToUse || undefined,
          clientName,
          isPlatformLevel,
        });
      } catch (err) {
        console.error('[PortalAuthProvider] Error building portal user context:', err);
        if (!cancelled) {
          setPortalUser(null);
          setClientInfo({});
        }
      } finally {
        if (!cancelled) setPortalUserLoading(false);
      }
    };

    fetchPortalUserData();

    return () => {
      cancelled = true;
    };
  }, [
    mainAuth.user?.id,
    mainAuth.user?.email,
    mainAuth.session?.access_token,
    isViewingAsAdmin,
    clientViewSession?.client_id,
    clientViewSession?.client_name,
    clientViewSession?.started_at,
  ]);

  // Login - delegates to main auth
  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  }, []);

  // Logout - delegates to main auth
  const logout = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  // Refresh session
  const refreshSession = useCallback(async () => {
    const { error } = await supabase.auth.refreshSession();
    return !error;
  }, []);

  // Update profile
  const updateProfile = useCallback(async (updates: Partial<PortalAuthUser>) => {
    if (!mainAuth.user?.id) return false;

    const { error } = await supabase
      .from('profiles')
      .update({
        first_name: updates.firstName,
        last_name: updates.lastName,
      })
      .eq('id', mainAuth.user.id);

    if (!error && portalUser) {
      setPortalUser({ ...portalUser, ...updates });
    }

    return !error;
  }, [mainAuth.user?.id, portalUser]);

  // Build context value
  const value: PortalAuthContextType = useMemo(() => ({
    user: portalUser,
    session: mainAuth.session ? {
      accessToken: mainAuth.session.access_token,
      refreshToken: mainAuth.session.refresh_token,
      expiresAt: mainAuth.session.expires_at || 0,
    } : null,
    isAuthenticated: !!mainAuth.user,
    isLoading: mainAuth.loading || portalUserLoading,
    error: null,
    login,
    logout,
    refreshSession,
    updateProfile,
  }), [
    portalUser,
    mainAuth.session,
    mainAuth.user,
    mainAuth.loading,
    portalUserLoading,
    login,
    logout,
    refreshSession,
    updateProfile,
  ]);

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
};

export default PortalAuthProvider;
